import {
  downloadMultipleFilesWithConcurrency,
  DownloadResult,
  listBlobsInFolder
} from './blobUploadService'
import { RaceTeam } from '../types/types'
import { WorkflowRunGroup } from '@aero-platform/shared'

/**
 * Build the blob path for a case's log directory
 * Format: cases/<caseNumber>/Logs (without raceTeamId prefix as that's added by server)
 * Strips the "0-" prefix from case numbers if present (e.g., "0-105" -> "105")
 */
export function buildLogPath(raceTeamId: string, caseNumber: string): string {
  // Strip the "0-" prefix if present (systemRunId format is "0-105", but storage path is "cases/105/Logs")
  const cleanCaseNumber = caseNumber.startsWith('0-') ? caseNumber.substring(2) : caseNumber
  return `cases/${cleanCaseNumber}/Logs`
}

/**
 * Get the storage account name from a RaceTeam object
 */
export function getStorageAccountName(raceTeam: RaceTeam | null | undefined): string | null {
  if (!raceTeam?.blobStorage?.accountName) {
    return null
  }
  return raceTeam.blobStorage.accountName
}

/**
 * Build the full Azure Blob Storage URL for a case's logs
 * Format: https://<accountName>.blob.core.windows.net/cfd-cases/<raceTeamId>/cases/<caseNumber>/Logs
 */
export function buildLogStorageUrl(
  accountName: string,
  raceTeamId: string,
  caseNumber: string
): string {
  const logPath = buildLogPath(raceTeamId, caseNumber)
  return `https://${accountName}.blob.core.windows.net/${logPath}`
}

/**
 * Extract case number from WorkflowRunGroup
 * The systemRunId contains the case number
 */
export function getCaseNumber(workflowRunGroup: WorkflowRunGroup): string | null {
  return workflowRunGroup.systemRunId || null
}

/**
 * Download log files for a specific case
 *
 * @param generateSasUrl API function to get SAS URLs
 * @param raceTeamId The race team ID
 * @param caseNumber The case number (systemRunId)
 * @param logFileNames Array of log file names to download (e.g., ['starccm.log', 'error.log'])
 * @returns Promise with download results
 */
export async function downloadCaseLogs(
  generateSasUrl: (params?: any) => Promise<any>,
  raceTeamId: string,
  caseNumber: string,
  logFileNames: string[]
): Promise<DownloadResult[]> {
  // Build full blob paths for each log file
  const blobPaths = logFileNames.map(
    fileName => `${buildLogPath(raceTeamId, caseNumber)}/${fileName}`
  )

  // Download all log files with concurrency control
  return await downloadMultipleFilesWithConcurrency(
    generateSasUrl,
    'permanent', // or 'cfd-cases' if that's a specific type on your server
    blobPaths,
    null,
    undefined
  )
}

/**
 * Download a single log file and save it to user's computer
 *
 * @param generateSasUrl API function to get SAS URLs
 * @param raceTeamId The race team ID
 * @param caseNumber The case number (systemRunId)
 * @param logFileName Name of the log file to download
 * @param customFileName Optional custom name for the downloaded file
 */
export async function downloadSingleCaseLog(
  generateSasUrl: (params?: any) => Promise<any>,
  raceTeamId: string,
  caseNumber: string,
  logFileName: string,
  customFileName?: string
): Promise<Blob> {
  console.log('[LogDownloadService] Starting downloadSingleCaseLog', {
    raceTeamId,
    caseNumber,
    logFileName,
    customFileName,
    timestamp: new Date().toISOString()
  })

  const { downloadFileDirectFromBlob } = await import('./blobUploadService')

  const blobPath = `${buildLogPath(raceTeamId, caseNumber)}/${logFileName}`

  console.log('[LogDownloadService] Blob path constructed:', blobPath)
  console.log(
    '[LogDownloadService] Calling downloadFileDirectFromBlob with uploadType: CASE_LOG_FILE'
  )

  try {
    const blob = await downloadFileDirectFromBlob(
      generateSasUrl,
      'CASE_LOG_FILE',
      blobPath,
      null,
      undefined
    )

    console.log('[LogDownloadService] Successfully downloaded blob', {
      blobSize: blob.size,
      blobType: blob.type,
      blobPath
    })

    return blob
  } catch (error: any) {
    console.error('[LogDownloadService] Error downloading blob:', {
      error: error?.message,
      stack: error?.stack,
      blobPath,
      raceTeamId,
      caseNumber,
      logFileName
    })
    throw error
  }
}

/**
 * List log files available for a specific case
 * Uses blobUploadService which calls backend API to avoid CORS issues
 *
 * @param generateSasUrl API function to generate SAS URLs for blob access
 * @param raceTeamId The race team ID
 * @param caseNumber The case number (systemRunId)
 * @returns Promise with array of log file names
 */
export async function listCaseLogFiles(
  generateSasUrl: (params?: any) => Promise<any>,
  raceTeamId: string,
  caseNumber: string
): Promise<string[]> {
  console.log('[LogDownloadService] listCaseLogFiles called', {
    raceTeamId,
    caseNumber,
    timestamp: new Date().toISOString()
  })

  try {
    // Build the log folder path
    const logFolderPath = buildLogPath(raceTeamId, caseNumber)

    console.log('[LogDownloadService] Listing blobs via blobUploadService', {
      logFolderPath
    })

    // Use the blob upload service to list files (which calls backend API)
    const fileNames = await listBlobsInFolder(
      generateSasUrl,
      'CASE_LOG_FILE', // uploadType for backend API
      logFolderPath,
      null, // aeroTempRunId not needed for logs
      undefined // gmid not needed
    )

    console.log('[LogDownloadService] Log files discovered', {})

    return fileNames
  } catch (error: any) {
    console.error('[LogDownloadService] Error listing log files', {
      raceTeamId,
      caseNumber,
      error: error?.message,
      stack: error?.stack
    })
    throw error
  }
}
